import React, { useState } from 'react';
import { Upload, Sparkles, AlertTriangle, ShieldAlert, Lock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { FloatingElements } from './FloatingElements';
import { downloadSampleReport } from '../../utils/sampleReportGenerator';
interface HeroSectionProps {
  h1Override?: React.ReactNode;
}
export const HeroSection = ({
  h1Override
}: HeroSectionProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };
  const handleDownloadSampleReport = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadSampleReport();
    } catch (error) {
      console.error('Failed to download sample report:', error);
    } finally {
      setIsDownloading(false);
    }
  };
  return <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 bg-[#050505] overflow-hidden min-h-[90vh] flex flex-col justify-center" data-id="element-2655">
      <FloatingElements data-id="element-2656" />

      {/* Grid Background Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none z-0" data-id="element-2657" />

      {/* Hero Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center" data-id="element-2658">
        {/* Animated Badge */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="inline-flex items-center gap-2 bg-[#FF0000]/10 text-[#FF0000] px-4 py-1.5 rounded-full border border-[#FF0000]/30 mb-8 shadow-[0_0_15px_rgba(255,0,0,0.15)] backdrop-blur-sm" data-id="element-2659">
          <motion.div animate={{
          opacity: [1, 0.5, 1]
        }} transition={{
          duration: 1.5,
          repeat: Infinity
        }} data-id="element-2660">
            <AlertTriangle size={14} strokeWidth={2.5} data-id="element-2661" />
          </motion.div>
          <span className="font-mono text-[10px] md:text-xs font-bold tracking-widest uppercase" data-id="element-2662">
            Forensic AI Analysis Active
          </span>
        </motion.div>

        {/* Main Headline */}
        <motion.h1 initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-8 leading-[0.9] tracking-tighter uppercase" data-id="element-2663">
          {h1Override || <>
              Your Brain Is{' '}
              <span className="relative inline-block" data-id="element-2664">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-b from-gray-200 to-gray-500" data-id="element-2665">
                  Lying
                </span>
                <span className="absolute top-1/2 left-0 w-full h-[4px] md:h-[6px] bg-[#FF0000] -translate-y-1/2 rotate-[-2deg]" data-id="element-2666" />
              </span>{' '}
              To You.
              <br data-id="element-2667" />
              <span className="text-[#FF0000] drop-shadow-[0_0_30px_rgba(255,0,0,0.4)]" data-id="element-2668">
                Our AI Isn't.
              </span>
            </>}
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="text-lg md:text-xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed font-mono" data-id="element-2669">
          ToxicDetector is an advanced{' '}
          <span className="text-[#FFD700] font-bold" data-id="element-2670">
            AI toxic relationship detector
          </span>{' '}
          designed to uncover hidden manipulation, gaslighting, and emotional
          abuse patterns in digital conversations. Upload your texts and let
          forensic AI see the tactical gaslighting you've been programmed to
          ignore.
        </motion.p>

        {/* Red Flag Visual Proof */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="relative max-w-[500px] mx-auto mb-12 group" data-id="element-2671">
          <div className="absolute -inset-1 bg-[#FF0000]/20 rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-2672" />
          <div className="relative rounded-xl overflow-hidden border border-[#FF0000]/30 shadow-[0_0_30px_rgba(255,0,0,0.2)] bg-black/50 backdrop-blur-sm" data-id="element-2673">
            <img src="https://cdn.magicpatterns.com/uploads/6fWHFbe1LodeRZcxwg7PYR/file_00000000970c722f86dc01c42d5e2c9f_(1).png" alt="Toxicity Detected 92% - Red Flag Analysis" className="w-full h-auto object-cover opacity-90 hover:opacity-100 transition-opacity" fetchPriority="high" width={500} height={400} decoding="async" data-id="element-2674" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4 flex justify-between items-end" data-id="element-2675">
              <span className="text-[#FF0000] font-mono text-[10px] tracking-[0.2em] font-bold uppercase flex items-center gap-2" data-id="element-2676">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF0000] animate-pulse shadow-[0_0_5px_#FF0000]" data-id="element-2677" />
                Real Scan Result
              </span>
              <span className="text-white/50 font-mono text-[10px] tracking-widest" data-id="element-2678">
                ID: #8X92-A
              </span>
            </div>
          </div>
        </motion.div>

        {/* Scanner Widget */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="relative max-w-2xl mx-auto mb-16" data-id="element-2679">
          {/* Scanner Frame */}
          <div className="absolute -inset-1 bg-gradient-to-r from-[#FF0000] via-[#FFD700] to-[#FF0000] rounded-2xl opacity-20 blur-xl animate-pulse" data-id="element-2680" />

          <div className="relative bg-[#0a0a0a] rounded-xl border border-[#FF0000]/30 p-1 overflow-hidden shadow-2xl" data-id="element-2681">
            {/* Corner Markers */}
            <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#FF0000] z-20 rounded-tl-lg" data-id="element-2682" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#FF0000] z-20 rounded-tr-lg" data-id="element-2683" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#FF0000] z-20 rounded-bl-lg" data-id="element-2684" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#FF0000] z-20 rounded-br-lg" data-id="element-2685" />

            <div className="bg-[#050505] rounded-lg p-8 md:p-12 text-center relative overflow-hidden group" data-id="element-2686">
              {/* SCANNING LINE ANIMATION */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-[#FF0000] shadow-[0_0_20px_#FF0000,0_0_10px_#FF0000] z-10 animate-[scan_3s_ease-in-out_infinite]" data-id="element-2687" />
              <style data-id="element-2688">{`
                @keyframes scan {
                  0% { top: 0%; opacity: 0; }
                  10% { opacity: 1; }
                  90% { opacity: 1; }
                  100% { top: 100%; opacity: 0; }
                }
              `}</style>

              {/* Grid Overlay inside scanner */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,0,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,0,0,0.05)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" data-id="element-2689" />

              <input type="file" id="file-upload" className="hidden" onChange={handleFileChange} accept="image/*,.pdf" data-id="element-2690" />
              <label htmlFor="file-upload" className="cursor-pointer block relative z-20 group/label" data-id="element-2691">
                <motion.div animate={{
                scale: [1, 1.05, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} className="inline-flex items-center justify-center w-20 h-20 bg-[#FF0000]/10 rounded-full mb-6 border border-[#FF0000]/50 shadow-[0_0_20px_rgba(255,0,0,0.2)] group-hover/label:bg-[#FF0000]/20 transition-colors" data-id="element-2692">
                  <Upload className="w-8 h-8 text-[#FF0000]" strokeWidth={2} data-id="element-2693" />
                </motion.div>

                <h3 className="text-2xl font-bold text-white mb-3 uppercase tracking-wide group-hover/label:text-[#FF0000] transition-colors" data-id="element-2694">
                  Drop Evidence Here
                </h3>
                <p className="text-gray-400 text-sm mb-8 font-mono max-w-md mx-auto" data-id="element-2695">
                  Analyzing for manipulation, narcissism, and "The Silent Drain"
                  patterns.
                </p>

                {selectedFile ? <div className="inline-flex items-center gap-2 bg-[#FF0000]/20 border border-[#FF0000] px-4 py-2 rounded-lg text-[#FF0000] font-mono text-sm font-bold" data-id="element-2696">
                    <ShieldAlert size={16} data-id="element-2697" />
                    <span data-id="element-2698">EVIDENCE LOADED: {selectedFile.name}</span>
                  </div> : <div className="inline-flex items-center gap-2 text-xs text-gray-500 font-mono border border-gray-800 px-4 py-2 rounded-lg bg-black/50 backdrop-blur-sm" data-id="element-2699">
                    <Lock size={12} data-id="element-2700" />
                    <span className="tracking-wider" data-id="element-2701">
                      100% ANONYMOUS & ENCRYPTED
                    </span>
                  </div>}
              </label>
            </div>
          </div>

          {/* Scanner Status Text */}
          <div className="flex justify-between items-center mt-4 px-2" data-id="element-2702">
            <span className="text-[10px] text-[#FF0000] font-mono animate-pulse flex items-center gap-2 tracking-widest uppercase" data-id="element-2703">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF0000] shadow-[0_0_5px_#FF0000]" data-id="element-2704"></span>
              System Active
            </span>
            <span className="text-[10px] text-gray-600 font-mono tracking-widest uppercase" data-id="element-2705">
              V.2.4.0 Forensic Engine
            </span>
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.8
      }} className="flex flex-col sm:flex-row gap-6 justify-center items-center" data-id="element-2706">
          <a href="/signup" className="group relative px-8 py-4 bg-[#FF0000] text-white font-bold text-lg uppercase tracking-wider overflow-hidden rounded-xl hover:bg-red-600 transition-all shadow-[0_0_20px_rgba(255,0,0,0.3)] hover:shadow-[0_0_40px_rgba(255,0,0,0.5)] min-w-[260px] hover:-translate-y-1" data-id="element-2707">
            <div className="absolute inset-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" data-id="element-2708"></div>
            <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-2709">
              Start Forensic Scan
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-2710" />
            </span>
          </a>

          <button onClick={handleDownloadSampleReport} disabled={isDownloading} className="group px-8 py-4 bg-transparent border border-gray-800 text-gray-400 font-bold text-lg uppercase tracking-wider rounded-xl hover:border-[#FFD700] hover:text-[#FFD700] transition-all min-w-[260px] hover:-translate-y-1 hover:bg-[#FFD700]/5 inline-block text-center disabled:opacity-50 disabled:cursor-wait" data-id="element-2711">
            <span data-id="element-2712">
              {isDownloading ? 'Generating PDF...' : 'View Sample Report'}
            </span>
          </button>
        </motion.div>

        {/* Social Proof */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 1
      }} className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8 text-xs sm:text-sm text-gray-500 font-mono tracking-wide" data-id="element-2713">
          <div className="flex items-center gap-3" data-id="element-2714">
            <span className="relative flex h-2 w-2" data-id="element-2715">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FFD700] opacity-75" data-id="element-2716"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FFD700]" data-id="element-2717"></span>
            </span>
            <span className="text-gray-400" data-id="element-2718">
              <span className="text-[#FFD700] font-bold" data-id="element-2719">500+</span> SCANS THIS
              HOUR
            </span>
          </div>
          <div className="hidden sm:block w-px h-4 bg-gray-800" data-id="element-2720" />
          <div data-id="element-2721">
            <span className="text-white font-bold" data-id="element-2722">4.9/5</span> ACCURACY RATING
          </div>
          <div className="hidden sm:block w-px h-4 bg-gray-800" data-id="element-2723" />
          <div className="flex items-center gap-2" data-id="element-2724">
            <Lock size={12} className="text-gray-600" data-id="element-2725" />
            <span data-id="element-2726">AES-256 ENCRYPTION</span>
          </div>
        </motion.div>
      </div>
    </section>;
};